// app_core/src/area-selector/index.js

import { AREA_SELECTOR, LOGGING, IMAGE_PATHS, MAP_DATA_STRATEGY, MAP_SETTINGS, APP_VERSION } from '/settings.js';
import { renderCarousel } from './carousel.js';
import { updateUrl } from '../utils/url-utils.js';
import { getText } from '../translations/translations.js';
import { createLanguageSwitcher } from '../components/LanguageSelector.js';

// Cache for areas data
let cachedAreas = null;
const STORAGE_KEY_AREA = 'selected_area_slug';

/**
 * Initializes and shows the Area Selector.
 * @param {Object} options
 * @param {Function} options.onAreaSelected - Callback to run after area selection (e.g. show headers).
 */
export async function initializeAreaSelector(options = {}) {
    const { onAreaSelected } = options;

    // 1. Check if enabled
    if (!AREA_SELECTOR.ENABLED) {
        console.warn('Area Selector is disabled in settings.');
        if (onAreaSelected) onAreaSelected(null);
        return;
    }

    // Always update the URL to the root when showing the area selector
    if (window.location.pathname !== '/' && window.location.pathname !== '') {
        window.history.replaceState({}, '', '/');
    }

    // 2. Create/Get Container
    let container = document.getElementById('area-selector-container');
    if (!container) {
        container = document.createElement('div');
        container.id = 'area-selector-container';
        container.classList.add(`bg-${AREA_SELECTOR.BACKGROUND || 'solid'}`);

        // Start hidden to allow fade-in
        container.classList.add('hidden');

        document.body.appendChild(container);
    }

    // Clear previous content
    container.innerHTML = '';

    // 3. Fetch Data
    try {
        const areas = await fetchAreas();

        // 4. Render Content Wrapper
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'area-selector-content';

        // Logo and Title Container
        const titleContainer = document.createElement('div');
        titleContainer.className = 'area-selector-title-container';

        // Add Logo (logo_full.png)
        const logo = document.createElement('img');
        logo.className = 'area-selector-logo';
        // Use logo_full.webp from domain's images folder (fallback to PNG if not available)
        const resolveFullLogo = () => {
            if (IMAGE_PATHS?.LOGO) {
                // Support both legacy PNG and new WEBP LOGO paths
                const base = IMAGE_PATHS.LOGO.replace('logo.png', '').replace('logo.webp', '');
                return `${base}logo_full.webp`;
            }
            return '/images/logo/logo_full.webp';
        };
        const logoPath = resolveFullLogo();
        // Add version for cache-busting and to match preloaded assets
        const versionedLogoPath = (() => {
            if (!APP_VERSION) return logoPath;
            const separator = logoPath.includes('?') ? '&' : '?';
            return `${logoPath}${separator}v=${APP_VERSION}`;
        })();
        logo.src = versionedLogoPath;
        logo.alt = 'Logo';
        titleContainer.appendChild(logo);

        // Title
        const title = document.createElement('h2');
        title.className = 'area-selector-title';
        const titleText = await getText('area_selector.title') || '';
        title.textContent = titleText;
        titleContainer.appendChild(title);

        contentWrapper.appendChild(titleContainer);

        // Subtitle - "Choose your destination" (separate from title container)
        const subtitle = document.createElement('p');
        subtitle.className = 'area-selector-subtitle';
        const subtitleText = await getText('area_selector.subtitle') || 'Please choose your destination';
        subtitle.textContent = subtitleText;
        contentWrapper.appendChild(subtitle);

        // Language Switcher (Top Right) - Use global LanguageSelector (ONE SOURCE OF TRUTH)
        // This will trigger a global languageChanged event and update the whole app (with reload)
        // Patch: When changing language from area selector, clear selected area before reload
        const langSwitcher = createLanguageSwitcher({
            // Intercept reload to clear selected area
            onBeforeReload: () => {
                try {
                    localStorage.removeItem('selected_area_slug');
                } catch (e) {
                    // Ignore errors
                }
            }
        });
        if (langSwitcher) {
            langSwitcher.classList.add('area-selector-lang-switcher');
            container.appendChild(langSwitcher);
        }

        // Helper function to update carousel centering based on card count
        const updateCarouselCentering = (carousel) => {
            if (!carousel) return;
            const cardCount = carousel.querySelectorAll('.area-card').length;
            if (cardCount <= 2) {
                carousel.classList.add('center-cards');
            } else {
                carousel.classList.remove('center-cards');
            }
        };

        // Listen for languageChanged to update only dynamic content in this overlay
        // (Global UI is updated by translations.js)
        const onLanguageChanged = async () => {
            // Update only dynamic content in area selector overlay
            const titleText = await getText('area_selector.title') || '';
            title.textContent = titleText;
            const subtitleText = await getText('area_selector.subtitle') || 'Please choose your destination';
            subtitle.textContent = subtitleText;
            const footer = contentWrapper.querySelector('.area-selector-footer');
            if (footer) {
                footer.textContent = await getText('area_selector.footer_text');
            }
            // Re-render Carousel (to update area names)
            const oldCarousel = contentWrapper.querySelector('.area-carousel');
            if (oldCarousel) oldCarousel.remove();
            if (AREA_SELECTOR.STYLE === 'carousel') {
                await renderCarousel(areas, contentWrapper, handleSelection);
                const newCarousel = contentWrapper.querySelector('.area-carousel');
                updateCarouselCentering(newCarousel);
            }
        };
        window.addEventListener('languageChanged', onLanguageChanged);

        // 5. Render Style (Carousel/List/Search)
        const handleSelection = async (area) => {
            await selectArea(area, container, onAreaSelected);
        };

        if (AREA_SELECTOR.STYLE === 'carousel') {
            await renderCarousel(areas, contentWrapper, handleSelection);
            const carousel = contentWrapper.querySelector('.area-carousel');
            updateCarouselCentering(carousel);
        } else {
            console.warn(`Area Selector style '${AREA_SELECTOR.STYLE}' not implemented yet. Using Carousel.`);
            await renderCarousel(areas, contentWrapper, handleSelection);
            const carousel = contentWrapper.querySelector('.area-carousel');
            updateCarouselCentering(carousel);
        }

        // Footer Text (Optional)
        if (AREA_SELECTOR.SHOW_FOOTER_TEXT) {
            const footerText = await getText('area_selector.footer_text');
            if (footerText) {
                const footer = document.createElement('p');
                footer.className = 'area-selector-footer';
                footer.textContent = footerText;
                contentWrapper.appendChild(footer);
            }
        }

        container.appendChild(contentWrapper);

        // Trigger Fade-In
        // Force reflow to ensure the browser registers the 'hidden' state before removing it
        void container.offsetHeight;
        requestAnimationFrame(() => {
            container.classList.remove('hidden');
        });

    } catch (err) {
        console.error('Failed to initialize Area Selector:', err);
        // Fallback: if error, maybe just hide selector and let app try to continue?
        container.classList.add('hidden');
        if (onAreaSelected) onAreaSelected(null);
    }
}

/**
 * Fetches area data from the API.
 */
async function fetchAreas() {
    if (cachedAreas) return cachedAreas;

    // Use cached fetch to prevent duplicate requests
    const { fetchWithCache } = await import('../utils/cache/index.js');
    const data = await fetchWithCache('/app_core/api/sqlite/spots-area-selector.php');

    if (data.error) throw new Error(data.error);

    cachedAreas = data;
    return data;
}

/**
 * Handles the selection logic:
 * 1. Save to LocalStorage & URL
 * 2. Move Map IMMEDIATELY (Behind the overlay)
 * 3. Trigger Callback (Update Header Icon)
 * 4. Hide Selector Overlay (Reveal Map)
 */
async function selectArea(area, container, callback) {
    if (LOGGING.ENABLED) console.log('[Area Selector] Selected:', area.slug);

    // 1. Save to LocalStorage & URL
    try {
        localStorage.setItem(STORAGE_KEY_AREA, area.slug);
    } catch (e) {
        console.warn('Failed to save area selection to localStorage', e);
    }
    updateUrl(area.slug, null);

    // 2. Move Map IMMEDIATELY (Behind the overlay)
    // We import dynamically to avoid circular dependencies if any, 
    // although map.js should be initialized by now.
    const { getMap, loadGeoJSONMap, updateMapStyles } = await import('../map/map.js');
    const { clearAllSpots } = await import('../map/map-visibility.js');

    const map = getMap();

    // For PER_AREA strategy, load the specific map data for this area
    // The overlay is still up, so we can load before moving/revealing
    if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.TYPE === 'PER_AREA') {
        if (LOGGING.ENABLED) console.log(`[Area Selector] Loading map data for area: ${area.slug}`);
        // This will fetch /data/{slug}/... and render it
        await loadGeoJSONMap(map, area.slug);
    }

    // For UNIFIED strategy, update styles to highlight selected area and dim others
    if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.TYPE === 'UNIFIED') {
        if (LOGGING.ENABLED) console.log(`[Area Selector] Updating map styles for area: ${area.slug}`);
        updateMapStyles(area.slug);
    }

    if (map && area.center_lat && area.center_lng) {
        // Clear any existing spots so we don't see old spots on new area
        clearAllSpots();
        // Instant jump. No animation. The overlay hides the jump.
        map.setView([area.center_lat, area.center_lng], area.zoom || 10, { animate: false });

        // Apply Max Bounds from DB if in PER_AREA mode
        // This ensures the user cannot pan outside the specific area
        if (MAP_DATA_STRATEGY && MAP_DATA_STRATEGY.TYPE === 'PER_AREA') {
            if (area.bounds_north && area.bounds_south && area.bounds_east && area.bounds_west) {
                const bounds = [
                    [area.bounds_south, area.bounds_west], // SouthWest
                    [area.bounds_north, area.bounds_east]  // NorthEast
                ];

                // Update map options
                if (MAP_SETTINGS.MAX_BOUNDS_VISCOSITY) {
                    map.options.maxBoundsViscosity = MAP_SETTINGS.MAX_BOUNDS_VISCOSITY;
                }

                map.setMaxBounds(bounds);

                if (LOGGING.ENABLED) {
                    console.log(`[Area Selector] Applied max bounds for ${area.slug}:`, bounds);
                }
            } else {
                // If no bounds defined for this area, remove any previous restrictions
                map.setMaxBounds(null);
            }
        }
    }

    // 3. Trigger Callback (to show headers/buttons and update icons)
    if (callback) callback(area);

    // 4. Small delay to allow map to render new tiles behind the curtain
    await new Promise(r => setTimeout(r, 100));

    // 5. Reveal the new map (Fade out overlay)
    container.classList.add('hidden');
}

/**
 * Helper to find an area object by slug from the cache (or fetch if needed).
 * Useful for deep-linking logic in App.js.
 */
export async function getAreaBySlug(slug) {
    const areas = await fetchAreas();
    return areas.find(a => a.slug === slug);
}

/**
 * Retrieves the last selected area slug from localStorage.
 */
export function getLastSelectedAreaSlug() {
    return localStorage.getItem(STORAGE_KEY_AREA);
}
